import { CognitoUserSession } from 'amazon-cognito-identity-js';

import { getCurrentSession, userHasSignedIn as userHasSignedInOnCognito } from 'remote/auth';
import { Cognito } from 'utils/cognito';
import { hasAcceptedCookies, isRecentCookieTimestamp } from 'utils/cookies';
import { init as initDataDogLogs } from 'utils/datadog';
import { RBIEnv, brand, env, isLocalDev, platform } from 'utils/environment';
import { initGoogleTagManager } from 'utils/google-tag-manager';
import LaunchDarklyHelper, { LaunchDarklyFlag } from 'utils/launchdarkly';
import logger from 'utils/logger';
import LogRocketHelper from 'utils/logrocket';

interface GtmEvent {
  event: string;
  brand: string;
  timestamp: number;
  duration: number;
  platform: string;
}

declare global {
  interface Window {
    dataLayer: GtmEvent[];
    LOADING_START_TIME: number;
    /**
     * window.Cypress exists when our app is running within Cypress
     * Places we currently check for this:
     * - frontend/src/state/graphql/links/index.ts - avoid BatchHttpLink
     *
     * https://docs.cypress.io/faq/questions/using-cypress-faq.html#Is-there-any-way-to-detect-if-my-app-is-running-under-Cypress
     */
    Cypress?: any;
    // When run with cypress for cypress-v2 package,
    // we instantiate the LD client with predefined flags
    _initial_cypress_feature_flags?: object;
    // Skip the interval for checking for unavailable items.
    // Avoids race condition in cart with recorded tests
    _skipUnavailableItemsIntervalCheck?: boolean;
  }
}

const initLaunchDarkly = async (): Promise<Record<string, unknown>> => {
  const ldClient = await LaunchDarklyHelper.init();

  return new Promise<Record<string, unknown>>((resolve, reject) => {
    ldClient.on('initialized', () => {
      const allFlags = ldClient.allFlags();
      const flattenedFlags = LaunchDarklyHelper.flattenFlags(allFlags);
      const ddSampleRate = flattenedFlags[LaunchDarklyFlag.DATADOG_LOG_SAMPLE_RATE];
      const logRocketFlag = flattenedFlags[LaunchDarklyFlag.ENABLE_LOGROCKET];
      const cookieBannerFlag = flattenedFlags[LaunchDarklyFlag.ENABLE_COOKIE_BANNER];
      const isCookieVersioningEnabled = flattenedFlags[LaunchDarklyFlag.ENABLE_COOKIE_VERSIONING];
      const cookiesAccepted = isCookieVersioningEnabled
        ? hasAcceptedCookies()
        : isRecentCookieTimestamp();

      initDataDogLogs(ddSampleRate);

      if (logRocketFlag && (!cookieBannerFlag || cookiesAccepted)) {
        LogRocketHelper.init();
      }

      resolve(flattenedFlags);
    });

    ldClient.on('failed', () => reject(new Error('LD Init failed')));
    ldClient.on('error', error => reject(error));
  }).catch(error => {
    logger.error(error);
    return {};
  });
};

const initCognitoSession = async (): Promise<CognitoUserSession | null> => {
  Cognito.configure();
  return getCurrentSession();
};

const emitLoadingSuccessEvent = (rbiBrand: string, rbiPlatform: string) => {
  const successTime = performance.now?.();
  const durationTillSuccess = successTime - window.LOADING_START_TIME;
  (window.dataLayer || []).push({
    event: 'APP_BUNDLE_LOADING_SUCCESS',
    brand: rbiBrand,
    timestamp: successTime,
    duration: durationTillSuccess,
    platform: rbiPlatform,
  });
};

export default async function bootstrap(
  renderApp: ({
    flags,
    userHasSignedIn,
    userSession,
  }: {
    flags: Record<string, unknown>;
    userHasSignedIn: boolean;
    userSession: CognitoUserSession | null;
  }) => void
): Promise<void> {
  const [ENV, BRAND, PLATFORM] = [env(), brand().toUpperCase(), platform()];

  if (!isLocalDev) {
    emitLoadingSuccessEvent(BRAND, PLATFORM);
  }

  const [flags, userSession] = await Promise.all([initLaunchDarkly(), initCognitoSession()]);

  // this has to be after init cognito session.
  const userHasSignedIn = await userHasSignedInOnCognito();

  if (ENV === RBIEnv.TEST) {
    const defaultFlags = {};
    renderApp({
      flags: defaultFlags,
      userHasSignedIn,
      userSession,
    });
    return;
  }

  initGoogleTagManager();

  renderApp({
    flags,
    userHasSignedIn,
    userSession,
  });
}
